'use client';

import { Fragment } from 'react';
import Link from 'next/link';
import styles from './DeliveryManager.module.scss';
import {
  ArrowRight,
  CheckCircle2,
  Code2,
  Database,
  LayoutTemplate,
  Target,
  User,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { gaCta } from '@/src/helpers/gtag';
import { useAOS } from '@/src/lib/hooks/useAOS';

interface Skill {
  label: string;
  icon: 'strategy' | 'code' | 'data' | 'product';
}

interface Metric {
  value: string;
  label: string;
  icon: 'users' | 'skills' | 'check';
}

interface DeliveryManagerProps {
  eyebrow?: string;
  title?: string;
  highlightedTitle?: string;
  description?: string;
  clientLabel?: string;
  managerLabel?: string;
  resultLabel?: string;
  pricingLabel?: string;
  skills?: Skill[];
  metrics?: Metric[];
  ctaLabel?: string;
  ctaHref?: string;
  backgroundImage?: string;
  tilt?: 'left' | 'right';
  id?: string;
}

const SKILL_ICONS: Record<Skill['icon'], LucideIcon> = {
  strategy: Target,
  code: Code2,
  data: Database,
  product: LayoutTemplate,
};

const METRIC_ICONS: Record<Metric['icon'], LucideIcon> = {
  users: User,
  skills: Users,
  check: CheckCircle2,
};

const DEFAULT_SKILLS: Skill[] = [
  { label: 'Stratégie', icon: 'strategy' },
  { label: 'Code', icon: 'code' },
  { label: 'Data', icon: 'data' },
  { label: 'Produit', icon: 'product' },
];

const DEFAULT_METRICS: Metric[] = [
  { value: '1', label: 'interlocuteur unique', icon: 'users' },
  { value: '4+', label: 'compétences mobilisables', icon: 'skills' },
  { value: '0€', label: 'de surcoût par profil', icon: 'check' },
];

const renderTitle = (text: string) => {
  const parts = text.split('*');
  return parts.map((part, i) =>
    i % 2 === 1 ? <em key={i}>{part}</em> : <Fragment key={i}>{part}</Fragment>
  );
};

export function DeliveryManager({
  eyebrow = 'NOTRE APPROCHE',
  title = 'Vous n’embauchez pas une équipe.',
  highlightedTitle = '*Vous embauchez un delivery manager.*',
  description = 'C’est lui qui mobilise et ajuste les bonnes compétences en temps réel. Pas de surcoût par profil : vous ne payez qu’au résultat.',
  clientLabel = 'Vous',
  managerLabel = 'Delivery manager',
  resultLabel = 'Résultat livré',
  pricingLabel = 'Pas au forfait, au résultat',
  skills = DEFAULT_SKILLS,
  metrics = DEFAULT_METRICS,
  ctaLabel = 'Nous rencontrer',
  ctaHref = '/contact',
  backgroundImage,
  tilt = 'left',
  id,
}: DeliveryManagerProps) {
  useAOS();

  const tiltClass = tilt === 'left' ? styles.tiltLeft : styles.tiltRight;
  const hasBgClass = backgroundImage ? styles.hasBg : '';

  return (
    <div
      id={id}
      className={`${styles.root} ${tiltClass} ${hasBgClass}`}
      style={backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : undefined}
    >
      <div className={styles.bg} aria-hidden="true" />
      <div className={styles.inner}>
        <div className={styles.text} data-aos="fade-up" data-aos-duration="700">
          {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
          <h2 className={styles.title}>
            {renderTitle(title)}{' '}
            <span className={styles.highlight}>{renderTitle(highlightedTitle)}</span>
          </h2>
          <p className={styles.description}>{description}</p>

          <div className={styles.metrics}>
            {metrics.map((metric) => {
              const Icon = METRIC_ICONS[metric.icon];
              return (
                <div key={metric.label} className={styles.metricCard}>
                  <span className={styles.metricIcon}>
                    <Icon size={18} strokeWidth={1.5} />
                  </span>
                  <div className={styles.metricBody}>
                    <span className={styles.metricValue}>{metric.value}</span>
                    <span className={styles.metricLabel}>{metric.label}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <em className={styles.pricingLine}>{pricingLabel}</em>
          {ctaLabel && ctaHref && (
            <Link
              href={ctaHref}
              className={styles.cta}
              onClick={() => gaCta(ctaLabel, 'delivery_manager_cta', ctaHref)}
            >
              {ctaLabel}
            </Link>
          )}
        </div>

        <div
          className={styles.visual}
          data-aos="fade-up"
          data-aos-duration="900"
          data-aos-delay="150"
          aria-label="Schéma du delivery manager au centre des compétences"
        >
          <div className={styles.board}>
            <div className={styles.flow}>
              <div className={styles.clientCard} data-aos="fade-up" data-aos-delay="0">
                <span className={styles.cardIcon}>
                  <User size={22} strokeWidth={1.5} />
                </span>
                <span className={styles.cardLabel}>{clientLabel}</span>
              </div>

              <div className={styles.arrow} aria-hidden="true" data-aos="fade" data-aos-delay="80">
                <ArrowRight size={20} />
              </div>

              <div className={styles.managerCard} data-aos="zoom-in" data-aos-delay="160">
                <span className={styles.managerIcon}>
                  <Users size={28} strokeWidth={1.5} />
                </span>
                <span className={styles.managerLabel}>{managerLabel}</span>
                <span className={styles.managerCaption}>Mobilise & pilote</span>
              </div>

              <div className={styles.arrow} aria-hidden="true" data-aos="fade" data-aos-delay="240">
                <ArrowRight size={20} />
              </div>

              <div className={styles.resultCard} data-aos="fade-up" data-aos-delay="320">
                <span className={styles.cardIcon}>
                  <CheckCircle2 size={22} strokeWidth={1.5} />
                </span>
                <span className={styles.cardLabel}>{resultLabel}</span>
              </div>
            </div>

            <div className={styles.skills}>
              {skills.map((skill) => {
                const Icon = SKILL_ICONS[skill.icon];
                return (
                  <span key={skill.label} className={styles.skillChip}>
                    <Icon size={14} strokeWidth={1.5} />
                    {skill.label}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
